import React, { FC, memo } from "react";
import { zeroPad } from "react-countdown";
import { useTheme } from "@mui/material/styles";
import { palette, radius, spacing, withAlpha } from "@/themes/tokens";

interface CountdownTilesProps {
    header?: string;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed?: boolean;
    // Compact tiles for the small grid card.
    size?: "regular" | "compact";
    // Dashes instead of numbers while the countdown is not known yet (keeps the same size).
    placeholder?: boolean;
    // Shown on the right of the header line (e.g. the reserve badge on compact cards).
    headerRight?: React.ReactNode;
}

const units = [
    ["days", "Days"],
    ["hours", "Hrs"],
    ["minutes", "Min"],
    ["seconds", "Sec"],
] as const;

// Countdown shown as four tinted tiles in the client's brand colour. Same as the mobile app's CountdownTiles.
const CountdownTiles: FC<CountdownTilesProps> = (props) => {
    const { header, completed, size = "regular", placeholder = false, headerRight } = props;
    const primary = useTheme().palette.primary.main;
    const compact = size === "compact";

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: compact ? 4 : spacing.sm }}>
                <span style={{ fontSize: compact ? 10 : 12, fontWeight: 600, color: palette.textMuted }}>{header || " "}</span>
                {headerRight}
            </div>
            <div style={{ display: "flex", gap: compact ? 4 : spacing.sm }}>
                {units.map(([key, label]) => (
                    <div
                        key={key}
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: compact ? "4px 0" : "8px 0",
                            borderRadius: compact ? radius.sm : radius.md,
                            backgroundColor: withAlpha(primary, 0.1),
                        }}
                    >
                        <span style={{ fontSize: compact ? 13 : 18, fontWeight: 700, lineHeight: 1.25, color: primary, fontVariantNumeric: "tabular-nums" }}>
                            {placeholder ? "--" : completed ? "00" : zeroPad(props[key])}
                        </span>
                        <span style={{ fontSize: compact ? 8.5 : 10, fontWeight: 500, color: palette.textMuted, marginTop: compact ? 0 : 1 }}>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default memo(CountdownTiles);
